import { IModalScope } from "angular-ui-bootstrap";
import { IConnectionData, IInsight, IInsightsQueryParams } from "../../../models/models";
import { InsightService } from "@gtmhub/insights";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ICollection } from "@gtmhub/models";
import { IBrowseInsightsContext, IBrowseConnectionsActionsParams, IBuildInsightActionParams, IExecuteInsightActionParams, IChangeStateActionParams } from "../models";

export interface IBrowseInsightsScope extends IModalScope {
  connectionData: IConnectionData;
  indicators: {
    getting: boolean;
  };
  currentPage: number;
  totalPages: number;
  isUserSearching: boolean;
  scalarInsights: IInsight[];
  onPageChange(page: number): void;
  changePageNumber(page: number): void;
  clearInsightSearchField(): void;
  goBack(): void;
  getInsights(insightName?: string): void;
  goToInsightsBrowser(): void;
  openInsightBuilder(): void;
  selectInsight(insight: IInsight): void;
}

const itemsPerPage = 20;

export class BrowseInsightsCtrl {
  public static $inject = ["$scope", "browseInsightsContext", "InsightService", "UIErrorHandlingService"];

  constructor(
    private $scope: IBrowseInsightsScope,
    private browseInsightsContext: IBrowseInsightsContext,
    private insightService: InsightService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.indicators = {
      getting: true,
    };
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.clearInsightSearchField = this.clearInsightSearchField;
    this.$scope.getInsights = this.getInsights;
    this.$scope.goBack = this.goBack;
    this.$scope.goToInsightsBrowser = this.goToInsightsBrowser;
    this.$scope.openInsightBuilder = this.openInsightBuilder;
    this.$scope.selectInsight = this.selectInsight;

    this.usedConnectors = [this.browseInsightsContext.connectionData.name];
    this.init();
  }

  private fields = "id,title,description,name,fieldName,formatting{metadata,editor},usedFilters,queryLayout";
  private usedConnectors: Array<string>;
  private searchedInsightsByName: string;

  private init = () => {
    this.$scope.connectionData = this.browseInsightsContext.connectionData;
    this.$scope.currentPage = 1;
    this.$scope.isUserSearching = false;

    this.getInsights();
  };

  private goBack = (): void => {
    const params: IBrowseConnectionsActionsParams = {
      action: "browseConnections",
    };

    this.$scope.$close(params);
  };

  private goToInsightsBrowser = (): void => {
    const params: IChangeStateActionParams = {
      action: "changeState",
      stateName: "gtmhub.insightboards.widgets.browser",
    };

    this.$scope.$dismiss(params);
  };

  private getInsights = (insightName?: string) => {
    this.$scope.indicators.getting = true;

    if (insightName && insightName !== this.searchedInsightsByName) {
      this.$scope.currentPage = 1;
    }

    const queryParams: IInsightsQueryParams = {
      fields: this.fields,
      skip: (this.$scope.currentPage - 1) * itemsPerPage,
      take: itemsPerPage,
      isScalar: true,
      stringSearch: insightName || null,
      usedConnectors: this.usedConnectors,
    };

    this.insightService.getInsightsV2(queryParams).then(
      (scalarInsights: ICollection<IInsight>) => {
        this.$scope.indicators.getting = false;

        this.searchedInsightsByName = insightName;
        this.$scope.scalarInsights = scalarInsights.items;

        this.$scope.totalPages = Math.ceil(scalarInsights.totalCount / itemsPerPage);
        this.$scope.isUserSearching = !!insightName;
        this.setAutofocus();
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "getting", inline: true })
    );
  };

  private clearInsightSearchField = (): void => {
    this.$scope.currentPage = 1;
    this.$scope.indicators.getting = true;
    this.searchedInsightsByName = null;
    this.getInsights(null);
  };

  private onPageChange = (page: number) => {
    this.$scope.currentPage = page;
    this.$scope.indicators.getting = true;

    if (this.searchedInsightsByName) {
      this.getInsights(this.searchedInsightsByName);
    } else {
      this.getInsights(null);
    }
  };

  private openInsightBuilder = (): void => {
    const params: IBuildInsightActionParams = {
      action: "buildInsight",
      connectionData: this.browseInsightsContext.connectionData,
    };

    this.$scope.$close(params);
  };

  private selectInsight = (insight: IInsight) => {
    const params: IExecuteInsightActionParams = {
      action: "executeInsight",
      insight: { ...insight, connection: this.browseInsightsContext.connectionData },
    };

    this.$scope.$close(params);
  };

  private setAutofocus = () => {
    this.$scope.$broadcast("focusTextInput");
  };
}
